/**
 Copyright (c) 2021 Oracle and/or its affiliates.
 The Universal Permissive License (UPL), Version 1.0
 */

'use strict';

define('vb/components/oj-vb-fragment/fragmentViewModel',['knockout',
  'vb/private/log',
  'vb/private/utils',
  'vb/private/constants',
  'ojs/ojhtmlutils',
  '../oj-vb-fragment-param/loader',
  '../oj-vb-fragment-slot/loader',
], (ko, Log, Utils, Constants, HtmlUtils) => {
  /** @typedef {import('ojs/ojcomposite').PropertyChangedContext} PropertyChangedContext */
  const logger = Log.getLogger('/vb/components/oj-vb-fragment');
  const createComponentBridge = (factory, props) => factory.create(Constants.ComponentBridgeType.FRAGMENT, props);

  /**
   * ViewModel class for the oj-vb-fragment CCA.
   * @category Component
   */
  class FragmentViewModel {
    constructor(context) {
      this.log = logger;
      const props = context.properties;
      this.composite = context.element;
      this.id = props.id || this.composite.getAttribute('id');
      if (!props.name) {
        const errMsg = 'A fragment name was not provided. This is required in order to load and render a fragment.'
          + ' Check the configuration of the oj-vb-fragment CCA!';
        throw new Error(errMsg);
      }

      this.name = props.name;
      this.autoGeneratedId = false;
      if (!this.id) {
        this.id = `frag-${Utils.generateUniqueId()}`;
        // Generated ids are only stable for this CCA instance, so the runtime fragment cannot be safely reused later.
        this.autoGeneratedId = true;
        this.composite.setAttribute('id', this.id);
        this.log.info('fragment component', this.name, 'does not have the \'id\' property to be set! A system',
          'generated id will be used instead', this.id);
      }

      if (props.bridge) {
        // if a 'bridge' property is configured on the oj-vb-fragment (fragments in VDOM components will need to
        // configure this property and we also prefer authors move to always setting this property on all fragment
        // CCAs), then that is our hook into VB
        this.bridge = createComponentBridge(props.bridge, props);
      } else {
        const fullId = this.id ? `id:'${this.id}';name:'${this.name}'` : `name:'${this.name}'`;
        this.log.warn('the \'bridge\' property was not configured on the oj-vb-fragment component with', fullId,
          '. For best results it is recommended that the \'bridge\' property be set to "[[ vbBridge ]]".');

        // Otherwise locate the 'bridge' property setup on the outer context. But the outer context, which might be
        // another fragment, layout or a page, is rarely provided to the CCA; so one of these approaches is needed
        // to locate the same. To fetch the outer binding context there are 2 cases to consider:
        //
        //  i. VDOM: VDOM dynamic ui components are one of the few components which use template compilation so that
        //    calling ko.contextFor in shared code won't work! In order to get the context dynamic component
        //    requires fragment to support a special binding property 'dataContext' that allows dynamic-layout
        //    context to be passed-through. Other components already pass through the outer binding context so it
        //    shouldn't be an issue(?).
        //
        //  ii. Web Components: The ko.contextFor would apply for all other cases outside of dynamic UI.  When
        //  fragment is wrapped in regular CCA, Max is ok with you using ko.contextFor, at least until JET core can
        //  provide a proper alternative.
        //
        this.dataContext = props.dataContext ? props.dataContext : ko.contextFor(this.composite).$data;

        // the bridge on the parent scope is used to tie the current fragment to its parent container.
        this.bridge = createComponentBridge(this.dataContext[Constants.componentBridge], props);
      }

      // Create the writeback listener once so the same callback can be reused across connect/disconnect cycles.
      // Register it only while connected so the shared parent bridge does not retain stale fragment component state
      // after oj-bind-if / oj-bind-for-each removes the fragment from the DOM.
      this.variableValueChangedListener = this.paramChangedCallback();
      this.variableValueChangedListenerRegistered = false;
      this.params = {};
      this.buildParams();
      this.gatherSlots();
    }

    /**
     *  This code fetches the vb-fragment-param child elements and reads properties when the viewModel is
     *  being created.
     *  For each of those params, it creates an array entry object and adds it to the this.params object.
     */
    buildParams() {
      const params = {};
      const addParam = (node) => {
        const name = node.getProperty('name');
        const value = node.getProperty('value');
        const valueExpr = node.getAttribute('value');
        const writebackMode = node.getAttribute('writebackMode') || 'default';

        const cloneVal = Utils.cloneObject(value);

        params[name] = { value: cloneVal, valueExpr, writebackMode };

        // generate a uid for the param node as well. This is to be able to locate the node later when its value
        // needs to be updated (see paramChangedCallback)
        node.setAttribute('data-vb-uid', `${this.id}:${name}`);
      };

      const paramNodes = this.composite.getElementsByTagName('oj-vb-fragment-param') || [];

      // paramNodes is not an array but htmlElement Collection
      for (let i = 0; i < paramNodes.length; i += 1) {
        addParam(paramNodes[i]);
      }
      this.params = params;
    }

    /**
     * Composite lifecycle callback
     * Invoked after the View is first inserted into the DOM and then each time the composite is reconnected to the
     * DOM after being disconnected. Note that if the composite needs to add/remove event listeners, we recommend
     * using this and the disconnected methods.
     * @param context
     */
    connected(context) {
      this.log.info('vb-fragment component', this.name, 'connected to DOM');
      if (!this.variableValueChangedListenerRegistered) {
        // Register this instance on connect because the parent bridge is shared by sibling fragments.
        this.bridge.addVariableValueChangedListener(this.variableValueChangedListener);
        this.variableValueChangedListenerRegistered = true;
      }
      this.bridge.connected(context, this.id);
    }

    /**
     * Composite lifecycle callback called when the composite component is disconnected from the DOM.
     * Invoked when this composite component is disconnected from the DOM.
     * @param element
     */
    disconnected(element) {
      // TODO: perform cleanup here of fragment when the DOM is disconnected. We may need to determine what it means
      //  to disconnect/dispose a fragment in isolation. When a page is disposed its fragments are removed as well.
      //  it's hard to distinguish between disconnected called because page containing fragment navigates vs
      //  fragment being disconnected because of content no longer visible.
      this.log.info('vb-fragment component', this.name, 'disconnected from DOM');
      const domNodes = element ? element.children : undefined;
      if (this.variableValueChangedListenerRegistered) {
        // Remove only this instance's listener so disconnected fragments do not leak while sibling listeners stay
        // intact on the shared bridge.
        this.bridge.removeVariableValueChangedListener(this.variableValueChangedListener);
        this.variableValueChangedListenerRegistered = false;
      }
      this.bridge.disconnected(domNodes, this.id, element);
    }

    gatherSlots() {
      this.templates = this.composite.querySelectorAll('template[slot]');
    }

    /**
     * Gets the fragment's moduleConfig object. The view and viewModel are likely to be Promises that are fully
     * resolved when the view is retrieved  and model fully initialized/activated.
     * @return a Promise that resolves with an object that represents the ojModule configuration for the fragment
     * - contains the resolved values for view and viewModel.
     * @see Fragment
     */
    getModuleConfig() {
      return this.bridge.getModuleConfig(this.id, this.name, this.params, this.templates,
        { autoGeneratedId: this.autoGeneratedId }).then((config) => {
        const configObj = ko.utils.unwrapObservable(config);
        // oj-module config can only be a Promise that resolves to a full-fledged object literal in order for
        // lifecycle callbacks to work! So wait until Promises resolve for view and viewModel
        const viewPromise = (configObj && configObj.view) || Promise.resolve();
        const viewModelPromise = (configObj && configObj.viewModel) || Promise.resolve();

        return Promise.all([viewPromise, viewModelPromise]).then((values) => {
          const viewNodeArray = HtmlUtils.stringToNodeArray(values[0]);
          // include other props in config besides the obvious
          return Object.assign(configObj, { view: viewNodeArray, viewModel: values[1] });
        });
      });
    }

    /**
     * Composite ViewModel callback called when a property on the composite is updated and before the
     * [property]Changed event is fired.
     * @param {PropertyChangedContext} context
     */
    propertyChanged(context) {
      if (context.property === 'name') {
        // writeback to (fragment) name is currently not supported. writeback is set to false by default but
        //  we still want to log error if author changes name on the fly
        const { previousValue, value } = context;
        if (value !== this.name) {
          this.log.error('The fragment', this.name, 'name property was changed from', previousValue, 'to', value,
            '. The fragment name on the CCA property cannot be changed dynamically.');
        }
      }
    }

    /**
     * Called when the sub-component vb-param configuration changes.
     * @param name {string} name of the param that changed
     * @param paramValue {*} an object with various properties including 'value'
     */
    updateParam(name, paramValue) {
      this.params[name] = Utils.cloneObject(paramValue);
      // update the param on the fragment
      this.bridge.updateParam(this.id, name, this.params[name]);
    }

    /**
     * listener called when the value of the (fragment) param is changed by the VB fragment container. Here we
     * explicitly call setProperty to notify fragment param CCA to update the value on the associated source variable
     */
    paramChangedCallback() {
      return (eventPayload = {}, fragmentId = this.id) => {
        if (eventPayload.diff) {
          // eslint-disable-next-line camelcase
          const { _vb_fragmentId, name, value } = eventPayload;
          // locate the target vb-fragment CCA node first and then the vb-fragment-param that matches the 'name', whose
          // source 'value' variable needs to be updated.
          // eslint-disable-next-line camelcase
          if (fragmentId === _vb_fragmentId) {
            const fragParamId = `${fragmentId}:${name}`;
            const paramNode = document.querySelector(`oj-vb-fragment-param[data-vb-uid="${fragParamId}"]`);

            // paramNode must have its 'name' property match the name. Use paramNode.name to match as it uses the
            // property getter to resolve the attributes. Using getAttribute is recommended for anything that
            // isn't a component property in general. While getAttribute() calls are made in other places in
            // fragmentViewModel, they are made early enough that it returns a valid value, but the call
            // is not guaranteed to yield a value "after the element has been connected to the DOM + had ko bindings
            // applied", particularly involving preact parents. In the case of the bug, getAttribute('name') was called
            // later in the component cycle and *not* returning even the mapped attribute (string) value.
            const found = paramNode ? paramNode['name'] === name : null;
            if (found) {
              // @ts-expect-error valueChangedProxy is provided by the fragment-param CCA at runtime.
              paramNode.valueChangedProxy('value', value);
            } else {
              this.log.info('Unable to locate fragment param', name, 'to set the new value', value, 'for fragment',
                fragmentId, '. This is likely, because no param was configured!');
            }
          }
        }
      };
    }
  }

  return FragmentViewModel;
});

